import { React } from "react"
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword, onAuthStateChanged } from "firebase/auth";
import { auth } from "../../utils/firebase";
 
import "./login.css";
function Login() {
  const baseurl = process.env.REACT_APP_API_URL;
  const [currentError, setCurrentError] = useState();

  // SECTION: Custom-plan login checkout state. The refs synchronously guard the
  // Firebase auth observer and submit handler, which can both run for one login.
  const [isProcessingCustomCheckout, setIsProcessingCustomCheckout] = useState(false);
  const customCheckoutInProgressRef = useRef(false);
  const authenticatedFlowHandledRef = useRef(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

    const navigate = useNavigate();
  const location = useLocation();

  // SECTION: A custom signup URL supplies only an opaque token. It remains in
  // the URL through the signup-to-login handoff; prices stay server-side.
  const customPlanToken = new URLSearchParams(location.search).get("custom_plan_token");
    const isCustomPlanLogin = Boolean(customPlanToken);

  useEffect(() => {
    // SECTION: React Router can reuse this component when only the query
    // string changes. Reset guards so a newly opened custom link is handled.
    customCheckoutInProgressRef.current = false;
    authenticatedFlowHandledRef.current = false;
    setIsProcessingCustomCheckout(false);
  }, [customPlanToken]);

  const startCustomCheckout = useCallback(async (user) => {
    // SECTION: Firebase invokes onAuthStateChanged during sign-in, so this
    // synchronous lock prevents duplicate Checkout Sessions and redirects.
    if (customCheckoutInProgressRef.current) {
      return true;
    }

    customCheckoutInProgressRef.current = true;
    setIsProcessingCustomCheckout(true);
    setCurrentError(undefined);

    try {
      const idToken = await user.getIdToken();
      const response = await fetch(`${baseurl}/api/create-custom-payment-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ custom_plan_token: customPlanToken }),
      });
      const data = await response.json();

      if (!response.ok || !data.url) {
        throw new Error(data.error || "Unable to create custom payment session");
      }

      // SECTION: Stripe Checkout is external to the SPA, so use a full-page
      // navigation rather than routing to the proposal dashboard prematurely.
      window.location.assign(data.url);
      return true;
    } catch (error) {
      console.error("Custom login checkout creation failed:", error);
      setCurrentError(error);
      customCheckoutInProgressRef.current = false;
      setIsProcessingCustomCheckout(false);
      return false;
    }
  }, [baseurl, customPlanToken]);

  const completeAuthenticatedFlow = useCallback(async (user) => {
    // SECTION: This handles both an interactive login and an already
    // authenticated visitor opening a custom login URL.
    if (!user || authenticatedFlowHandledRef.current) {
      return;
    }

    authenticatedFlowHandledRef.current = true;
    localStorage.setItem("hasRunAfterLogin", "true");
    localStorage.setItem("user_id", user.uid);

    if (isCustomPlanLogin) {
      const checkoutStarted = await startCustomCheckout(user);
      if (!checkoutStarted) {
        // SECTION: Allow the user to retry after an invalid, expired, or
        // temporarily unavailable custom Checkout request.
        authenticatedFlowHandledRef.current = false;
      }
      return;
    }

    // Standard login should navigate as soon as Firebase confirms the session.
    // The dashboard owns its data loading and keeps onboarding closed until the
    // client request has completed.
    navigate("/proposal-dashboard", { replace: true });
  }, [isCustomPlanLogin, navigate, startCustomCheckout]);

  useEffect(() => {
    // SECTION: Do not redirect a custom-plan user to the dashboard. Instead,
    // continue their trusted custom token flow into Stripe Checkout.
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        completeAuthenticatedFlow(user);
      }
    });
    return () => unsubscribe();
  }, [completeAuthenticatedFlow]);  
  
  

const onLogIn = async (e) => {
    e.preventDefault();
    setCurrentError(undefined);

    try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);

        // SECTION: The auth observer also calls this function. Its guard makes
        // this explicit call safe and prevents the observer redirect race.
        await completeAuthenticatedFlow(userCredential.user);
    } catch (error) {
        console.log(error);
        setCurrentError(error);
    }
};

  return (
    <div className="login-page">
      <div className="wrapper">
        <div className="title">Login Form</div>
        <div>
          {/* SECTION: Firebase errors use code; Checkout/API errors use message. */}
          {currentError && <p className="text-center text-red-700 my-2">{currentError.code ? currentError.code.replace("auth/", "") : currentError.message || "Unable to sign in"}</p>}
        </div>
        <form onSubmit={onLogIn}>
          <div className="field">
            <input
              type="text"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <label htmlFor="email">Email Address</label>
          </div>
          <div className="field">
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <label htmlFor="password">Password</label>
          </div>
          <div className="content">
            <div className="checkbox">
              <input type="checkbox" id="remember-me" />
              <label htmlFor="remember-me">Remember me</label>
            </div>
            <div className="pass-link">
              <Link to="/reset-password">Forgot password?</Link>
            </div>
          </div>
          <div className="field">
            {/* SECTION: Keep the user informed while custom Checkout is prepared. */}
            <input
              type="submit"
              id="submit Login"
              value={isProcessingCustomCheckout ? "Opening checkout..." : "Login"}
              disabled={isProcessingCustomCheckout}
              aria-disabled={isProcessingCustomCheckout}
            />
          </div>
          <div className="signup-link">
            Not a member?{" "}
            {/* SECTION: Retain the opaque custom token for users who need to
                create an account instead of logging in. */}
            <Link to={isCustomPlanLogin ? `/signup?custom_plan_token=${encodeURIComponent(customPlanToken)}` : "/signup"}>Sign Up</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Login;
